import AnalyticsFilterInputs, {
  AnalyticsFilterComparisonProperty,
} from "./AnalyticsFilterInputs";

/** Absolute date window chrome hint; recall still uses stored absolute dates. */
export type AnalyticsDateShortcut =
  | "LAST_30_DAYS"
  | "LAST_3_MONTHS"
  | "LAST_6_MONTHS"
  | "LAST_12_MONTHS"
  | "LAST_YEAR"
  | "YEAR_TO_DATE"
  | "LAST_FULL_YEAR"
  | "ALL_TIME"
  | "THIS_QUARTER"
  | "FIT_TO_JOURNEYS"
  | "CUSTOM"
  | null;

export interface AnalyticsCatalogueSelection {
  id: number;
  label: string;
}

export interface AnalyticsJourneySelection {
  key: string;
  label: string;
}

/** Journey + date window — page-bound when present on a saved filter. */
export interface AnalyticsJourneyFilterLayer {
  allJourneys: boolean;
  journeys: AnalyticsJourneySelection[];
  dateRange: { startDate: string | Date; endDate: string | Date } | null;
  dateShortcut?: AnalyticsDateShortcut;
}

/** Audience facets — portable across analytics pages. */
export interface AnalyticsAudienceFilterLayer {
  activeUsersOnly: boolean;
  includePartiallyApproved: boolean;
  locations: AnalyticsCatalogueSelection[];
  jobTitles: AnalyticsCatalogueSelection[];
  appraisalLevels: AnalyticsCatalogueSelection[];
  userGroups: AnalyticsCatalogueSelection[];
}

/** FilterJson schemaVersion 1 envelope. */
export interface AnalyticsSavedFilterDocument {
  schemaVersion: 1;
  sourcePageKey?: string | null;
  journey: AnalyticsJourneyFilterLayer | null;
  audience: AnalyticsAudienceFilterLayer;
  comparisonProperty: AnalyticsFilterComparisonProperty;
  /** UTC calendar date yyyy-MM-dd for Show past data, or null for live (ADR-0006). */
  pointInTimeDate: string | null;
}

export type AnalyticsSavedFilterVisibility = "PERSONAL" | "SHARED";

export interface AnalyticsSavedFilterDto {
  id: number;
  name: string;
  ownerEmployeeId: number;
  visibility: AnalyticsSavedFilterVisibility;
  schemaVersion: number;
  filterJson: string;
  sourcePageKey: string | null;
  isPortable: boolean;
  isPageDefault: boolean;
  canEdit: boolean;
  dateCreated: string;
  dateLastModified: string;
}

export function buildPageKey(
  dynamicPageId: number | undefined,
  pageIdentifier: string
): string {
  if (dynamicPageId != null) {
    return `DYNAMIC:${dynamicPageId}`;
  }
  if (pageIdentifier === "UsageReport") {
    return "USAGE";
  }
  return pageIdentifier;
}

export function idsFromCatalogue(
  items: AnalyticsCatalogueSelection[] | null | undefined
): number[] {
  return items?.map((x) => x.id) ?? [];
}

export function catalogueFromIds(
  ids: number[] | null | undefined,
  labelLookup: Map<number, string>
): AnalyticsCatalogueSelection[] {
  if (!ids || ids.length === 0) return [];
  return ids.map((id) => ({
    id,
    label: labelLookup.get(id) ?? String(id),
  }));
}

/** Map a saved document (or live two-layer state) onto widget API filter inputs. */
export function savedDocumentToFilterInputs(
  doc: AnalyticsSavedFilterDocument
): AnalyticsFilterInputs {
  const journeyKeys = doc.journey?.journeys.map((j) => j.key) ?? [];
  return {
    allJourneys: doc.journey?.allJourneys ?? false,
    journeyKeys,
    dateRange:
      doc.journey?.dateRange?.startDate && doc.journey?.dateRange?.endDate
        ? {
            startDate: new Date(doc.journey.dateRange.startDate),
            endDate: new Date(doc.journey.dateRange.endDate),
          }
        : null,
    activeUsersOnly: doc.audience.activeUsersOnly,
    includePartiallyApproved: doc.audience.includePartiallyApproved,
    comparisonProperty: doc.comparisonProperty,
    locationIds: idsFromCatalogue(doc.audience.locations),
    jobTitleIds: idsFromCatalogue(doc.audience.jobTitles),
    appraisalLevelIds: idsFromCatalogue(doc.audience.appraisalLevels),
    userGroupIds: idsFromCatalogue(doc.audience.userGroups),
    pointInTimeDate: doc.pointInTimeDate?.trim()
      ? doc.pointInTimeDate.trim()
      : null,
  };
}

export function parseSavedFilterDocument(
  filterJson: string
): AnalyticsSavedFilterDocument | null {
  try {
    const doc = JSON.parse(filterJson) as AnalyticsSavedFilterDocument;
    if (doc?.schemaVersion !== 1 || !doc.audience) return null;
    return doc;
  } catch {
    return null;
  }
}
